import React, { useRef, useLayoutEffect, useState, useEffect, useContext } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { DataContext } from '../../app/ApiContext';
import { CASHFREE_MODE } from '../../app/constant';
import { toast } from 'react-toastify';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import bgImage from '../../Assets/toolskillsection/tools_skills_bg.webp';
import DashboardLogo from '../../Assets/toolskillsection/dashboard_logo.webp';
import SubscriptionLogo from '../../Assets/toolskillsection/subscription_logo.webp';
import AlbumsLogo from '../../Assets/toolskillsection/albums_logo.webp';
import MembersLogo from '../../Assets/toolskillsection/members_logo.webp';
import PaymentLogo from '../../Assets/toolskillsection/payment_logo.webp';
import InquiryLogo from '../../Assets/toolskillsection/inquiry_logo.webp';
import EventLogo from '../../Assets/toolskillsection/event_logo.webp';
import PackageLogo from '../../Assets/toolskillsection/package_logo.webp';
import WhatsappLogo from '../../Assets/toolskillsection/whatsapp_logo.webp';
import GstLogo from '../../Assets/toolskillsection/gst_logo.webp';
import GalleryLogo from '../../Assets/toolskillsection/gallery_logo.webp';
import PlanCardLogo from '../../Assets/toolskillsection/plancard_logo.webp';

import { trackEvent } from '../../app/Firebase';

gsap.registerPlugin(ScrollTrigger);

/* ── Global fix: react-phone-input-2's actual scrollable list is `.country-list`,
   not the div our `dropdownStyle` prop targets. We isolate its scroll here so
   scrolling past the top/bottom of the list doesn't bleed through to the page
   behind the modal. ── */
const PhoneDropdownGlobalStyle = createGlobalStyle`
  .country-list {
    overscroll-behavior: contain;
    touch-action: pan-y;
    max-height: 220px;
  }
`;

// ─── Static plan definitions ───────────────────────────────────────────────────
const BASE_PLANS = [
  {
    title: 'Growth',
    cardLabel: 'GETTING STARTED',
    discount: '17% save on Yearly plans',
    buttonLabel: 'Get Started',
    monthlyPriceINR: 749,
    yearlyPriceINR: 6999,
    subtext: 'All plans include a 14-day free trial. No credit card required. Cancel anytime.',
    features: [
      { text: 'AI Face Scan', available: true },
      { text: '60 GB Storage', available: true },
      { text: 'Team Member Management', available: true },
      { text: 'Event Calendar Management', available: true },
      { text: 'Quotations & Invoicing', available: true },
      { text: 'Event & Studio Management', available: true },
      { text: 'Client Album Photos Selection', available: true },
    ],
  },
  {
    title: 'Starter',
    cardLabel: 'SOLO SHOOTER',
    discount: '17% save on Yearly plans',
    buttonLabel: 'Get Started',
    monthlyPriceINR: 1499,
    yearlyPriceINR: 14999,
    subtext: 'All plans include a 14-day free trial. No credit card required. Cancel anytime.',
    features: [
      { text: 'AI Face Scan', available: true },
      { text: '100 GB Storage', available: true },
      { text: 'Team Member Management', available: true },
      { text: 'Event Calendar Management', available: true },
      { text: 'Lead Management with automatic follow ups', available: true },
      { text: 'WhatsApp CRM', available: true },
      { text: 'Automatic Payments - UPI, Cards & More', available: true },
      { text: 'Quotations & Invoicing', available: true },
      { text: 'Event & Studio Management', available: true },
      { text: 'Client Album Photos Selection', available: true },
    ],
  },
  {
    title: 'Professional',
    cardLabel: 'MOST POPULAR',
    discount: '17% save on Yearly plans',
    buttonLabel: 'Get Started',
    monthlyPriceINR: 2999,
    yearlyPriceINR: 29999,
    subtext: 'All plans include a 14-day free trial. No credit card required. Cancel anytime.',
    features: [
      { text: 'Included all features in Starter', available: true },
      { text: '300 GB Storage', available: true },
      { text: 'Enable Watermark', available: true },
      { text: 'Customer Payment', available: true },
      { text: 'Accounting Management', available: true },
      { text: 'Priority Support', available: true },
      { text: 'Spark - Camera 2 cloud', available: true },
      { text: 'Customers/Team Members payments management', available: true },
      { text: 'Event & engagement tracking', available: true },
    ],
  },
  {
    title: 'Studio Group',
    cardLabel: 'MULTI-BRANCH',
    discount: '17% save on Yearly plans',
    buttonLabel: 'Contact Sales',
    monthlyPriceINR: 4999,
    yearlyPriceINR: 49999,
    subtext: 'All plans include a 14-day free trial. No credit card required. Cancel anytime.',
    features: [
      { text: 'Included all features in Professional', available: true },
      { text: '1 TB Storage', available: true },
      { text: 'Website Creation', available: true },
      { text: 'White-label Branding', available: true },
    ],
  },
];

// ─── Currency helpers ──────────────────────────────────────────────────────────
const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ',
  SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥', MYR: 'RM ',
  THB: '฿', ZAR: 'R ', SAR: 'SAR ', KWD: 'KD ', QAR: 'QAR ',
  BRL: 'R$', CHF: 'CHF ', NZD: 'NZ$', HKD: 'HK$', CNY: '¥',
  MXN: 'MX$', IDR: 'Rp ', PHP: '₱', TRY: '₺', RUB: '₽',
};

function formatPrice(amount, currency) {
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${Math.round(amount).toLocaleString()}`;
}

// ─── Static fallback rates (INR value of 1 unit of target currency) ───────────

const FALLBACK_INR_RATES = {
  USD: 83.5, EUR: 90.5, GBP: 105.8, AED: 22.7, SGD: 62.0,
  AUD: 55.5, CAD: 61.0, JPY: 0.56, MYR: 17.7, THB: 2.4,
  ZAR: 4.6, SAR: 22.3, KWD: 271.0, QAR: 22.9,
  BRL: 15.2, CHF: 95.0, NZD: 51.0, HKD: 10.7, CNY: 11.6,
  MXN: 4.5, IDR: 0.0053, PHP: 1.45, TRY: 2.5, RUB: 1.0,
};

// ─── Currency detection hook ───────────────────────────────────────────────────

function useCurrency() {
  const [currency, setCurrency] = useState('INR');
  const [rate, setRate] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        const geoRes = await fetch('https://ipapi.co/json/');
        if (!geoRes.ok) throw new Error('geo lookup failed');
        const geo = await geoRes.json();

        // geo.currency  -> "USD"    (ISO code, use this)
        // geo.currency_name -> "Dollar" (human label, NOT usable for conversion)
        const detectedCurrency = geo.currency || 'INR';
        if (cancelled) return;

        if (detectedCurrency === 'INR') {
          setLoading(false);
          return;
        }

        // Try live rate first
        try {
          const fxRes = await fetch(
            `https://api.frankfurter.app/latest?from=USD&to=INR,${detectedCurrency}`
          );
          if (!fxRes.ok) throw new Error('fx lookup failed');
          const fx = await fxRes.json();

          const usdToInr = fx.rates?.['INR'];
          const usdToTarget = fx.rates?.[detectedCurrency];

          if (usdToInr && usdToTarget) {
            if (!cancelled) {
              setRate(usdToTarget / usdToInr); // multiply INR price by this
              setCurrency(detectedCurrency);
            }
            return;
          }
          throw new Error('currency not supported by frankfurter');
        } catch {
          // Live rate failed (or unsupported currency) — use static fallback
          const fallbackInrPerUnit = FALLBACK_INR_RATES[detectedCurrency];
          if (fallbackInrPerUnit && !cancelled) {
            setRate(1 / fallbackInrPerUnit); // multiply INR price by this
            setCurrency(detectedCurrency);
          }
          // no fallback for this currency either -> silently stays on INR
        }
      } catch {
        // ipapi.co itself failed -> silently fall back to INR
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    detect();
    return () => { cancelled = true; };
  }, []);

  return { currency, rate, loading };
}

// ─── Tags data ─────────────────────────────────────────────────────────────────
const TAGS = [
  { label: 'Albums & Galleries', logo: AlbumsLogo, top: '33%', left: '26%', mobileTop: '33%', mobileLeft: '26%', angle: '-7deg', iconOffset: '0deg' },
  { label: 'Event Scheduling', logo: InquiryLogo, top: '32%', left: '66%', mobileTop: '32%', mobileLeft: '66%', angle: '4deg', iconOffset: '-4deg' },
  { label: 'Photo Editing Pipeline', logo: EventLogo, top: '53%', left: '20%', mobileTop: '53%', mobileLeft: '20%', angle: '-10deg', iconOffset: '5deg' },
  { label: 'My Packages', logo: SubscriptionLogo, top: '42%', left: '44%', mobileTop: '42%', mobileLeft: '44%', angle: '-6deg', iconOffset: '6deg' },
  { label: 'Event Notifications', logo: PaymentLogo, top: '50%', left: '61%', mobileTop: '50%', mobileLeft: '61%', angle: '-11deg', iconOffset: '-3deg' },
  { label: 'Payment Collection', logo: PackageLogo, top: '53%', left: '82%', mobileTop: '49%', mobileLeft: '82%', angle: '-5deg', iconOffset: '3deg' },
  { label: 'Business Dashboard', logo: DashboardLogo, top: '72%', left: '25%', mobileTop: '75%', mobileLeft: '25%', angle: '9deg', iconOffset: '-8deg' },
  { label: 'Team Management', logo: MembersLogo, top: '65%', left: '45%', mobileTop: '65%', mobileLeft: '45%', angle: '10deg', iconOffset: '-1deg' },
  { label: 'Quotation Builder', logo: PaymentLogo, top: '73%', left: '74%', mobileTop: '73%', mobileLeft: '74%', angle: '5deg', iconOffset: '-4deg' },
  { label: 'Album Lock & Protect', logo: GalleryLogo, top: '85%', left: '67%', mobileTop: '85%', mobileLeft: '50%', angle: '6deg', iconOffset: '-4deg' },
  { label: 'WhatsApp Leads', logo: WhatsappLogo, top: '85%', left: '20%', mobileTop: '85%', mobileLeft: '88%', angle: '-9deg', iconOffset: '4deg' },
  { label: 'GST Invoicing', logo: GstLogo, top: '85%', left: '40%', mobileTop: '60%', mobileLeft: '88%', angle: '-9deg', iconOffset: '4deg' },
];

// ─── Component ─────────────────────────────────────────────────────────────────
const NewToolsSkillsSection = () => {
  const dashboardRef = useRef(null);
  const plansSectionRef = useRef(null);
  const plansScrollRef = useRef(null);
  const tagRefs = useRef([]);
  const planCardRefs = useRef([]);
  const phoneFieldRef = useRef(null); // wraps the PhoneInput, used for the scroll-isolation safety net

  const { currency, rate, loading } = useCurrency();
  const { data, createOrder, completePayment, registerUser } = useContext(DataContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('payment');
  const [modalPlan, setModalPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [formData, setFormData] = useState({ name: '', studio_name: '', email: '', phone: '', countryCode: '+91', coupon: '' });
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const renderDiscount = (discount) => {
    const [highlight, ...rest] = discount.split(' on ');
    return (
      <>
        <DiscountHighlight>{highlight}</DiscountHighlight>
        {rest.length > 0 && `on ${rest.join(' on ')}`}
      </>
    );
  };

  // ── Match static plan title to backend plan ──────────────────────────────────
  const getBackendPlan = (staticTitle) => {
    const plans = Array.isArray(data?.data) ? data.data
      : Array.isArray(data) ? data
        : [];
    if (!plans.length) return null;

    // 1. Match by price (most reliable)
    const priceMap = {
      'Growth': { monthly: 749, yearly: 6999 },
      'Starter': { monthly: 1499, yearly: 14990 },
      'Professional': { monthly: 2999, yearly: 29999 },
      'Studio Group': { monthly: 4999, yearly: 49999 },
    };
    const target = priceMap[staticTitle];
    if (target) {
      const byPrice = plans.find(p =>
        Number(p.offer_price) === target.monthly ||
        Math.round(Number(p.storage_price_per_year)) === target.yearly
      );
      if (byPrice) return byPrice;
    }

    // 2. Match by keyword in description/name
    const keywordMap = {
      'Growth': ['growth'],
      'Starter': ['studio', 'starter', 'solo', 'basic'],
      'Professional': ['pro', 'professional'],
      'Studio Group': ['business', 'studio group', 'group', 'enterprise'],
    };
    const byKeyword = plans.find(p =>
      (keywordMap[staticTitle] || []).some(kw =>
        (p.description || '').toLowerCase().includes(kw) ||
        (p.name || '').toLowerCase().includes(kw)
      )
    );
    if (byKeyword) return byKeyword;

    // 3. Fallback by index position
    const indexMap = { 'Starter': 0, 'Professional': 1, 'Studio Group': 2 };
    return staticTitle in indexMap ? plans[indexMap[staticTitle]] || null : null;
  };

  // ── Razorpay SDK loader ──────────────────────────────────────────────────────
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  // ── Modal helpers ────────────────────────────────────────────────────────────
  const handleOpenModal = (plan) => {
    trackEvent('plan_select_click', {
      plan_name: plan.title,
      button_label: plan.buttonLabel,
    });

    setModalMode('payment');
    setModalPlan(plan);
    setBillingCycle('yearly');
    setFormData({ name: '', studio_name: '', email: '', phone: '', countryCode: '+91', coupon: '' });
    setFormErrors({});
    setSuccess(false);
    setModalOpen(true);
  };

  useEffect(() => {
    const handleOpenFreeTrial = () => {
      setModalMode('registration');
      setModalPlan({ title: 'Starter' });
      setBillingCycle('yearly');
      setFormData({ name: '', studio_name: '', email: '', phone: '', countryCode: '+91', coupon: '' });
      setFormErrors({});
      setSuccess(false);
      setModalOpen(true);
    };
    window.addEventListener('open-free-trial-modal', handleOpenFreeTrial);
    return () => window.removeEventListener('open-free-trial-modal', handleOpenFreeTrial);
  }, []);

  const handleCloseModal = () => {
    if (!loadingPayment) setModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  /* ── Scroll-isolation safety net ──────────────────────────────────────────────
     `overscroll-behavior: contain` (set globally above) handles this in all
     modern browsers. This wheel listener is an extra safety net for older
     browsers/webviews that don't fully respect that CSS property — it stops
     the wheel event from bubbling out of the dropdown list once the list
     itself has already consumed the scroll. ── */
  useEffect(() => {
    const wrapper = phoneFieldRef.current;
    if (!wrapper) return undefined;

    const handleWheel = (e) => {
      const list = wrapper.querySelector('.country-list');
      if (!list || !list.contains(e.target)) return;

      const { scrollTop, scrollHeight, clientHeight } = list;
      const atTop = scrollTop === 0;
      const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

      // Only block the page-level scroll when the list is at its edge and
      // the user keeps scrolling further in that direction — this is exactly
      // the moment scroll would otherwise "leak" through to the page.
      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        e.preventDefault();
      }
      e.stopPropagation();
    };

    wrapper.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', handleWheel);
  }, [modalOpen]);

  // ── Form validation ──────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';

    if (formData.studio_name && formData.studio_name.length > 120) {
      errors.studio_name = 'Studio name must be 120 characters or less';
    }

    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (formData.countryCode === '+91' && cleanPhone.length !== 10) {
        errors.phone = 'Indian phone numbers must be exactly 10 digits';
      } else if (formData.countryCode !== '+91' && (cleanPhone.length < 5 || cleanPhone.length > 15)) {
        errors.phone = 'Please enter a valid phone number';
      }
    }
    return errors;
  };

  // ── Registration-only handler ────────────────────────────────────────────────
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setLoadingPayment(true);
    setFormErrors({});

    try {
      const payload = {
        phone_number: formData.phone,
        country_code: formData.countryCode,
        name: formData.name,
        username: formData.name,
        role: 3,
      };

      if (formData.email?.trim()) {
        payload.email = formData.email.trim();
      }

      if (formData.studio_name?.trim()) {
        payload.studio_name = formData.studio_name.trim();
      }

      const regRes = await registerUser(payload);

      if (regRes?.error || regRes?.success === false) {
        throw new Error(regRes?.message || 'Registration failed.');
      }

      const token = regRes?.data?.access_token || regRes?.access_token;
      if (!token) throw new Error('Registration failed — no token received.');

      toast.success('Registration successful! 🎉');
      setSuccess(true);

    } catch (err) {
      toast.error(err.message || 'Registration failed.');
      setFormErrors({ submit: err.message });
    } finally {
      setLoadingPayment(false);
    }
  };

  // ── Main payment handler ─────────────────────────────────────────────────────
  const handleBuyNow = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setLoadingPayment(true);
    setFormErrors({});

    try {
      const backendPlan = getBackendPlan(modalPlan.title);
      if (!backendPlan) throw new Error('Plan not found on server.');

      const payload = {
        phone_number: formData.phone,
        country_code: formData.countryCode,
        name: formData.name,
        username: formData.name,
        role: 3,
      };

      if (formData.email?.trim()) {
        payload.email = formData.email.trim();
      }

      if (formData.studio_name?.trim()) {
        payload.studio_name = formData.studio_name.trim();
      }

      const regRes = await registerUser(payload);

      const token = regRes?.data?.access_token || regRes?.access_token;
      if (!token) throw new Error('Registration failed — no token received.');

      const orderRes = await createOrder({
        storage_plan_id: backendPlan.id,
        duration: billingCycle === 'yearly' ? 12 : 1,
        settlement_for: 'plan',
        yearly_plan: billingCycle === 'yearly',
        plan_discount: 0,
        return_url: 'https://app.kwikmemories.com/payment/return',
      }, token);

      const providerData = orderRes?.data?.data || orderRes?.data || {};

      const isCashfree = !!providerData?.payment_session_id;
      const isRazorpay = !!providerData?.id && providerData?.entity === 'order';

      if (!isCashfree && !isRazorpay) {
        throw new Error(
          orderRes?.message ||
          orderRes?.data?.message ||
          'Order creation failed — no payment session received.'
        );
      }

      if (isCashfree) {
        await handleCashfreePayment(providerData, backendPlan, token);
      } else {
        await handleRazorpayPayment(providerData, backendPlan, token);
      }

    } catch (err) {
      toast.error(err.message || 'Payment failed.');
      setFormErrors({ submit: err.message });
    } finally {
      setLoadingPayment(false);
    }
  };


  // ── Razorpay payment ─────────────────────────────────────────────────────────
  const handleRazorpayPayment = (providerData, backendPlan, token) => {
    return new Promise(async (resolve, reject) => {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) return reject(new Error('Razorpay SDK failed to load.'));

      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: providerData.amount,
        currency: providerData.currency || 'INR',
        name: 'Kwik Memories',
        description: backendPlan.description || modalPlan.title,
        order_id: providerData.id,
        prefill: { name: formData.name, email: formData.email, contact: formData.phone },
        theme: { color: '#AE2AB1' },
        handler: async (response) => {
          try {
            setLoadingPayment(true);
            await completePayment({
              plan_id: backendPlan.id,
              amount: providerData.amount / 100,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              order_id: providerData.id,
              settlement_for: 'plan',
              yearly_plan: billingCycle === 'yearly' ? 'yearly_plan' : null,
              landing_page: true,
            }, token);
            toast.success('Payment successful! 🎉');
            setSuccess(true);
            window.open('https://admin.kwikmemories.com/', '_blank');
            handleCloseModal();
            resolve();
          } catch (err) {
            toast.error('Payment verification failed: ' + err.message);
            reject(err);
          } finally {
            setLoadingPayment(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.warn('Payment cancelled.');
            setLoadingPayment(false);
            resolve();
          },
        },
      });

      rzp.on('payment.failed', (response) => {
        toast.error('Payment failed: ' + (response.error?.description || ''));
        reject(new Error(response.error?.description));
      });
      rzp.open();
    });
  };

  // ── Cashfree payment ─────────────────────────────────────────────────────────
  const handleCashfreePayment = async (providerData, backendPlan, token) => {
    const { load } = await import('@cashfreepayments/cashfree-js');

    if (!providerData.payment_session_id) {
      throw new Error('Cashfree payment_session_id missing.');
    }

    // Session id must match Cashfree env backend used (sandbox vs production).
    const environment = providerData?.environment || providerData?.mode || providerData?.cashfree_mode;
    const mode =
      environment === 'production' || environment === 'sandbox'
        ? environment
        : CASHFREE_MODE;
    const cashfree = await load({ mode });

    const result = await cashfree.checkout({
      paymentSessionId: providerData.payment_session_id,
      redirectTarget: '_modal',
    });

    if (result?.error) throw new Error(result.error?.message || 'Cashfree payment failed.');

    const status = result?.paymentDetails?.paymentStatus || result?.paymentDetails?.payment_status;
    if (status && status !== 'SUCCESS') throw new Error(`Payment not completed. Status: ${status}`);

    await completePayment({
      settlement_for: 'plan',
      payment_provider: 'cashfree',
      order_id: providerData.order_id || providerData.cf_order_id,
      plan_id: backendPlan.id,
      yearly_plan: billingCycle === 'yearly',
      plan_discount: 0,
      landing_page: true,
    }, token);

    toast.success('Payment successful! 🎉');
    setSuccess(true);
    window.open('https://admin.kwikmemories.com/', '_blank');
    handleCloseModal();
  };

  // ── Tags fall-in animation ───────────────────────────────────────────────────
  useLayoutEffect(() => {
    const tagEls = tagRefs.current.filter(Boolean);
    if (!tagEls.length) return;

    gsap.set(tagEls, { y: -420, opacity: 0, scale: 0.78 });
    const fallTl = gsap.timeline({ paused: true });
    fallTl.fromTo(tagEls,
      { y: -420, opacity: 0, scale: 0.78 },
      { y: 0, opacity: 1, scale: 1, duration: 1.0, ease: 'power3.out', stagger: { each: 0.13, from: 'start' } }
    );

    const st = ScrollTrigger.create({
      trigger: dashboardRef.current,
      start: 'top 85%',
      end: 'bottom top',
      onEnter: () => fallTl.play(0),
      onEnterBack: () => fallTl.play(0),
      onLeaveBack: () => gsap.set(tagEls, { y: -420, opacity: 0, scale: 0.78 }),
      invalidateOnRefresh: true,
      fastScrollEnd: true,
      preventOverlaps: 'page-sections',
    });

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    window.addEventListener('resize', refresh);
    return () => {
      window.removeEventListener('load', refresh);
      window.removeEventListener('resize', refresh);
      fallTl.kill();
      st.kill();
    };
  }, []);

  // ── Plan card 3-D scroll animation ──────────────────────────────────────────
  useLayoutEffect(() => {
    const plansSection = plansSectionRef.current;
    const planCards = planCardRefs.current.filter(Boolean);
    if (!plansSection || !planCards.length) return;

    const mm = gsap.matchMedia();

    const addCardAnimations = (yBase, yStep, start, end, scrub) => {
      gsap.set(planCards, { y: yBase, rotateX: 34, scale: 0.96, opacity: 0.88, transformPerspective: 1200, transformOrigin: '50% 100%' });
      planCards.forEach((card, i) => {
        gsap.fromTo(card,
          { y: yBase + i * yStep, rotateX: 34, scale: 0.96, opacity: 0.88, transformPerspective: 1200, transformOrigin: '50% 100%' },
          { y: 0, rotateX: 0, scale: 1, opacity: 1, ease: 'none', scrollTrigger: { trigger: card, start, end, scrub, invalidateOnRefresh: true, fastScrollEnd: true, preventOverlaps: 'page-sections' } }
        );
      });
    };

    mm.add('(min-width: 901px)', () => {
      const ctx = gsap.context(() => addCardAnimations(58, 10, 'top 92%', 'top 50%', 1), plansSection);
      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);
      return () => { window.removeEventListener('load', refresh); window.removeEventListener('resize', refresh); ctx.revert(); };
    });

    mm.add('(max-width: 900px)', () => {
      const ctx = gsap.context(() => addCardAnimations(48, 6, 'top 100%', 'top 65%', 0.45), plansSection);
      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);
      return () => { window.removeEventListener('load', refresh); window.removeEventListener('resize', refresh); ctx.revert(); };
    });

    return () => mm.revert();
  }, []);

  useLayoutEffect(() => {
    const el = plansScrollRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: 'auto' });
    const sync = () => el.scrollTo({ left: 0, behavior: 'auto' });
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  // ── Computed plans with converted prices ─────────────────────────────────────
  const plans = BASE_PLANS.map(plan => ({
    ...plan,
    monthlyPrice: loading ? '…' : formatPrice(plan.monthlyPriceINR * rate, currency),
    yearlyPrice: loading ? '…' : formatPrice(plan.yearlyPriceINR * rate, currency),
  }));

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <SectionWrapper id="tool-skill">
      <PhoneDropdownGlobalStyle />

      <SectionHeader>
        <Title>Tools & <TitleGradient>Skills</TitleGradient> Panel</Title>
        <Subtitle>TOOLS & SKILLS</Subtitle>
      </SectionHeader>

      <DashboardWrapper ref={dashboardRef}>
        {TAGS.map((tag, index) => (
          <TagAnimWrapper
            key={index}
            top={tag.top} left={tag.left}
            mobileTop={tag.mobileTop} mobileLeft={tag.mobileLeft}
            ref={(el) => { tagRefs.current[index] = el; }}
          >
            <FloatingTag angle={tag.angle}>
              <TagIcon src={tag.logo} alt={tag.label} $iconOffset={tag.iconOffset} />
              <TagLabel>{tag.label}</TagLabel>
            </FloatingTag>
          </TagAnimWrapper>
        ))}
      </DashboardWrapper>

      <PlansSectionContainer ref={plansSectionRef} id="pricing">
        <PlansLeftColumn>
          <PlansTitle>Flexible Plans For <br /><PlansTitleGradient>Every Stage</PlansTitleGradient></PlansTitle>
          <PlansSubtitle><PlansSubtitleDot /> CORE FEATURES <PlansSubtitleDot /></PlansSubtitle>
          <PlansSubtext>Start free. Scale as your studio grows. No hidden charges, no per-user fees.</PlansSubtext>
          {!loading && currency !== 'INR' && (
            <CurrencyBadge>🌍 Prices shown in {currency}</CurrencyBadge>
          )}
        </PlansLeftColumn>

        <PlansRightColumn ref={plansScrollRef}>
          {plans.map((plan, index) => (
            <PlanCard
              key={index}
              ref={(el) => { planCardRefs.current[index] = el; }}
              $isMiddleCard={index % 2 === 0}
            >
              <CardLabel>{plan.title === 'Professional' && '★ '}{plan.cardLabel}</CardLabel>

              <CardContent>
                <CardLeft>
                  <PlanHeader>
                    <BadgeRow>
                      <CrownBadge src={PlanCardLogo} alt="Plan logo" />
                      <DiscountBadge>{renderDiscount(plan.discount)}</DiscountBadge>
                    </BadgeRow>
                    <PlanMeta>
                      <PlanName>{plan.title}</PlanName>
                      <PriceContainer>
                        <PriceRow>
                          <PriceVal>{plan.monthlyPrice}</PriceVal>
                          <PricePeriod>/ Monthly-billed annually</PricePeriod>
                        </PriceRow>
                        <PriceRow>
                          <PriceVal>{plan.yearlyPrice}</PriceVal>
                          <PricePeriod>/ Yearly</PricePeriod>

                        </PriceRow>
                      </PriceContainer>
                    </PlanMeta>
                  </PlanHeader>
                </CardLeft>

                <CardRight $isMiddleCard={index % 2 === 0}>
                  <FeaturesList>
                    {plan.features.map((feature, fIdx) => (
                      <FeatureItem key={fIdx}>
                        <FeatureBullet $available={feature.available}>
                          {feature.available ? '✓' : '×'}
                        </FeatureBullet>
                        <FeatureText>{feature.text}</FeatureText>
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </CardRight>
              </CardContent>

              {/* <PlanSubtext>{plan.subtext}</PlanSubtext> */}
              <CardButtonWrapper>
                <ChoosePlanBtn onClick={() => handleOpenModal(plan)}>{plan.buttonLabel}</ChoosePlanBtn>
              </CardButtonWrapper>
            </PlanCard>
          ))}
        </PlansRightColumn>
      </PlansSectionContainer>

      {/* Checkout Modal */}
      <ModalOverlay $isOpen={modalOpen} onClick={handleCloseModal}>
        <ModalContent $isOpen={modalOpen} onClick={(e) => e.stopPropagation()}>
          {success ? (
            <SuccessContainer>
              <SuccessIcon>✓</SuccessIcon>
              <SuccessTitle>Thank You!</SuccessTitle>
              <SuccessText>
                {modalMode === 'registration' ? (
                  <>
                    Your registration is complete! You can now log in to the admin panel.
                    <br /><br />
                    Access your account here: <a href="https://admin.kwikmemories.com/auth/login" target="_blank" rel="noopener noreferrer" style={{ color: '#c94daa', fontWeight: 'bold' }}>https://admin.kwikmemories.com/auth/login</a>
                  </>
                ) : (
                  <>Your <strong>{modalPlan?.title}</strong> plan is active. You're all set!</>
                )}
              </SuccessText>
              {modalMode === 'registration' ? (
                <>
                  <CancelBtn onClick={() => { window.open('https://admin.kwikmemories.com/auth/login', '_blank'); handleCloseModal(); }} style={{ width: '100%', background: 'linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%)', color: '#fff' }}>Go to Admin Panel</CancelBtn>
                  <CancelBtn onClick={handleCloseModal} style={{ width: '100%', marginTop: '8px' }}>Close</CancelBtn>
                </>
              ) : (
                <CancelBtn onClick={handleCloseModal} style={{ width: '100%' }}>Close</CancelBtn>
              )}
            </SuccessContainer>
          ) : (
            <form onSubmit={modalMode === 'registration' ? handleRegistrationSubmit : handleBuyNow} noValidate>
              <ModalHeader>
                <ModalTitle>{modalMode === 'registration' ? 'Start Free Trial' : `${modalPlan?.title} Plan`}</ModalTitle>
                {modalMode === 'payment' && <PriceLabel>Select billing cycle</PriceLabel>}
              </ModalHeader>

              {modalMode === 'payment' && (
                <>
                  <RadioGroup>
                    <RadioOption $active={billingCycle === 'yearly'}>
                      <input type="radio" name="billingCycle" value="yearly"
                        checked={billingCycle === 'yearly'} onChange={() => setBillingCycle('yearly')} />
                      Yearly
                    </RadioOption>
                    <RadioOption $active={billingCycle === 'monthly'}>
                      <input type="radio" name="billingCycle" value="monthly"
                        checked={billingCycle === 'monthly'} onChange={() => setBillingCycle('monthly')} />
                      Monthly
                    </RadioOption>
                  </RadioGroup>

                  <PriceDisplay>
                    <PriceLabel>Total Price</PriceLabel>
                    <PriceValue>
                      {modalPlan && (loading ? '…' : formatPrice(
                        (billingCycle === 'yearly' ? modalPlan.yearlyPriceINR : modalPlan.monthlyPriceINR) * rate,
                        currency
                      ))}
                    </PriceValue>
                  </PriceDisplay>
                </>
              )}

              <FormField>
                <FormLabel htmlFor="name">Name <span style={{ color: '#000000' }}>*</span></FormLabel>
                <FormInput type="text" id="name" name="name" placeholder="Enter your name"
                  value={formData.name} onChange={handleInputChange} $error={!!formErrors.name} />
                {formErrors.name && <ErrorMsg>{formErrors.name}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel htmlFor="studio_name">Studio Name</FormLabel>
                <FormInput type="text" id="studio_name" name="studio_name" placeholder="Enter your studio name" maxLength={120}
                  value={formData.studio_name} onChange={handleInputChange} $error={!!formErrors.studio_name} />
                {formErrors.studio_name && <ErrorMsg>{formErrors.studio_name}</ErrorMsg>}
              </FormField>

              <FormField>
                <FormLabel htmlFor="email">Email</FormLabel>
                <FormInput type="email" id="email" name="email" placeholder="Enter your email"
                  value={formData.email} onChange={handleInputChange} $error={!!formErrors.email} />
                {formErrors.email && <ErrorMsg>{formErrors.email}</ErrorMsg>}
              </FormField>

              <FormField ref={phoneFieldRef}>
                <FormLabel htmlFor="phone">Phone Number <span style={{ color: '#000000' }}>*</span></FormLabel>
                <PhoneInput
                  country={'in'}
                  enableSearch={true}
                  searchPlaceholder="Search country..."
                  value={`${formData.countryCode.replace('+', '')}${formData.phone}`}
                  onChange={(value, country) => {
                    const dialCode = country.dialCode || '';
                    const phone = value.slice(dialCode.length);
                    setFormData(prev => ({ ...prev, phone: phone, countryCode: `+${dialCode}` }));
                    if (formErrors.phone) setFormErrors(prev => ({ ...prev, phone: '' }));
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '50px',
                    borderRadius: '10px',
                    border: formErrors.phone ? '1px solid #ff4d4f' : '1px solid #eaeaea',
                    background: '#fff',
                    fontSize: '15px',
                    color: '#101010',
                    fontFamily: 'inherit'
                  }}
                  buttonStyle={{
                    borderRadius: '10px 0 0 10px',
                    border: formErrors.phone ? '1px solid #ff4d4f' : '1px solid #eaeaea',
                    background: '#fff'
                  }}
                  dropdownStyle={{
                    fontFamily: 'inherit',
                    overscrollBehavior: 'contain',
                    zIndex: 9999
                  }}
                  placeholder="Enter your mobile number"
                />
                {formErrors.phone && <ErrorMsg>{formErrors.phone}</ErrorMsg>}
              </FormField>

              {modalMode === 'payment' && (
                <FormField>
                  <FormLabel htmlFor="coupon">Coupon Code</FormLabel>
                  <FormInput type="text" id="coupon" name="coupon" placeholder="Enter coupon code"
                    value={formData.coupon} onChange={handleInputChange} />
                </FormField>
              )}

              {formErrors.submit && <ErrorMsg style={{ display: 'block', marginBottom: 8 }}>{formErrors.submit}</ErrorMsg>}

              <ButtonRow>
                <CancelBtn type="button" onClick={handleCloseModal}>Cancel</CancelBtn>
                <BuyBtn type="submit" disabled={loadingPayment}>
                  {loadingPayment ? 'Processing…' : (modalMode === 'registration' ? 'Submit' : 'Buy Now')}
                </BuyBtn>
              </ButtonRow>
            </form>
          )}
        </ModalContent>
      </ModalOverlay>
    </SectionWrapper>
  );
};

export default NewToolsSkillsSection;

// ─── Styled Components ────────────────────────────────────────────────────────


const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
  margin-bottom: 20px;
`;


const CurrencyBadge = styled.div`
  display: inline-flex; align-items: center;
  margin-top: 20px; padding: 8px 18px; border-radius: 999px;
  background: linear-gradient(140.02deg, rgba(255,201,81,0.15) 2.22%, rgba(201,77,170,0.15) 96.12%);
  border: 1px solid rgba(201,77,170,0.25);
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-size: 14px; font-weight: 600; color: #c94daa; letter-spacing: 0.3px;
  @media (max-width: 768px) { font-size: 12px; padding: 6px 14px; margin-top: 14px; }
`;

const CardLabel = styled.div`
  position: absolute; top: -20px; left: 50%; transform: translateX(-50%);
  width: fit-content; min-width: 180px; text-align: center;
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 13px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
  color: #fff; background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  padding: 10px 24px; border-radius: 999px; white-space: nowrap; z-index: 2;
`;

const DiscountBadge = styled.span`
  display: inline-block;  padding: 6px 10px; border-radius: 12px;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  color: #fff; font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 13px; font-weight: 700; letter-spacing: 1px; width: 160px; text-align: center;

`;

const DiscountHighlight = styled.span`
  display: block;
  font-weight: 700;
  color: #000000;
`;

const SectionWrapper = styled.section`
  padding: 64px 64px 80px; background: #ffffff;
  display: flex; flex-direction: column; align-items: center; width: 100%; box-sizing: border-box;
  @media (max-width: 1024px) { padding: 48px 24px 64px; }
  @media (max-width: 768px)  { padding: 32px 16px 40px; }
  @media (max-width: 480px)  { padding: 24px 12px 32px; }
  @media (min-width: 1600px) { padding: 100px 100px 120px; }
`;

const SectionHeader = styled.div`
  text-align: center; margin-bottom: 56px;
  @media (max-width: 768px)  { margin-bottom: 34px; }
  @media (min-width: 1600px) { margin-bottom: 80px; }
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif; font-weight: 600; font-style: italic;
  font-size: 64px; color: #121212; margin: 0 0 12px; line-height: 1.2;
  @media (max-width: 768px)  { font-size: 36px; line-height: 1.08; margin-bottom: 8px; }
  @media (max-width: 480px)  { font-size: 30px; }
  @media (min-width: 1600px) { font-size: 80px; }
`;

const TitleGradient = styled.span`
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; color: transparent;
`;

const Subtitle = styled.div`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif; font-weight: 500;
  font-size: 28px; line-height: 140%; text-align: center; color: rgba(0,0,0,1);
  text-transform: uppercase; display: flex; align-items: center; justify-content: center; gap: 12px;
  @media (max-width: 1024px) { font-size: 30px; }
  @media (max-width: 768px)  { font-size: 18px; gap: 8px; }
  @media (max-width: 480px)  { font-size: 16px; gap: 6px; }
  @media (min-width: 1600px) { font-size: 34px; }
  &::before, &::after {
    content: '•';
    background: linear-gradient(140.02deg, #FFC951 2.22%, #C94DAA 96.12%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; color: transparent;
  }
`;

const DashboardWrapper = styled.div`
  position: relative; width: 100%; max-width: 1200px; margin: 0 auto;
  aspect-ratio: 16 / 9; background-image: url(${bgImage});
  background-size: cover; background-position: center;
  border-radius: 20px; box-shadow: 0 20px 50px rgba(0,0,0,0.08); box-sizing: border-box;
  @media (max-width: 768px)  { border-radius: 12px; }
  @media (min-width: 1600px) { max-width: 1600px; border-radius: 28px; }
`;

const TagAnimWrapper = styled.div`
  position: absolute; top: ${p => p.top}; left: ${p => p.left}; z-index: 10;
  @media (max-width: 768px) { top: ${p => p.mobileTop || p.top}; left: ${p => p.mobileLeft || p.left}; }
  @media (max-width: 345px) {
    &:nth-child(1){top:4%;left:8%} &:nth-child(2){top:13%;left:30%} &:nth-child(3){top:22%;left:62%}
    &:nth-child(4){top:31%;left:78%} &:nth-child(5){top:40%;left:10%} &:nth-child(6){top:49%;left:28%}
    &:nth-child(7){top:58%;left:60%} &:nth-child(8){top:67%;left:18%} &:nth-child(9){top:76%;left:58%}
  }
  @media (max-width: 320px) {
    &:nth-child(1){top:3%;left:8%} &:nth-child(2){top:12%;left:30%} &:nth-child(3){top:21%;left:62%}
    &:nth-child(4){top:30%;left:78%} &:nth-child(5){top:39%;left:10%} &:nth-child(6){top:48%;left:28%}
    &:nth-child(7){top:57%;left:60%} &:nth-child(8){top:66%;left:18%} &:nth-child(9){top:75%;left:58%}
  }
`;

const FloatingTag = styled.div`
  transform: translate(-50%,-50%) rotate(${p => p.angle || '0deg'});
  display: flex; align-items: center; gap: 12px;
  background-color: #ffffffd3;
  background-image: linear-gradient(165.02deg, rgba(255, 200, 81, 0.671) 2.22%, rgba(201, 77, 170, 0.651) 96.12%);
  color: #fff; padding: 6px 20px 6px 6px; border-radius: 10px; min-height: 48px;
  box-shadow: 0 10px 24px rgba(201,77,170,0.32); cursor: pointer; white-space: nowrap;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover { transform: translate(-50%,-50%) rotate(${p => p.angle || '0deg'}) scale(1.04); box-shadow: 0 12px 30px rgba(201,77,170,0.5); }
  @media (max-width: 1024px) { min-height: 42px; padding: 5px 16px 5px 5px; gap: 10px; border-radius: 12px; }
  @media (max-width: 768px)  { min-height: 30px; padding: 3px 10px 3px 3px; gap: 6px;  border-radius: 8px;  }
  @media (max-width: 480px)  { min-height: 24px; padding: 2px 6px  2px 2px; gap: 4px;  border-radius: 6px;  }
  @media (min-width: 1600px) { min-height: 64px; padding: 8px 24px 8px 8px; gap: 16px; border-radius: 14px; }
`;

const TagIcon = styled.img`
  width: 36px; height: 36px; object-fit: contain; flex-shrink: 0;
  border-radius: 10px; transform: rotate(${p => p.$iconOffset || '0deg'});
  @media (max-width: 1024px) { width: 32px; height: 32px; border-radius: 8px; }
  @media (max-width: 768px)  { width: 22px; height: 22px; border-radius: 6px; }
  @media (max-width: 480px)  { width: 16px; height: 16px; border-radius: 4px; }
  @media (min-width: 1600px) { width: 48px; height: 48px; border-radius: 12px; }
`;

const TagLabel = styled.span`
  font-family: 'Playfair Display';
  font-size: 20px; font-weight: 600; line-height: 100%; color: #0E1237;
  @media (max-width: 1024px) { font-size: 18px; }
  @media (max-width: 768px)  { font-size: 11px; }
  @media (max-width: 480px)  { font-size: 7px;  }
  @media (min-width: 1600px) { font-size: 26.63px; }
`;

const PlansSectionContainer = styled.div`
  display: flex; flex-direction: row; justify-content: space-between; align-items: flex-start;
  width: 100%; max-width: 1200px; margin: 0 auto; gap: 64px; box-sizing: border-box;
  @media (max-width: 1024px) { gap: 28px; margin-top: 72px; }
  @media (max-width: 900px)  { flex-direction: column; gap: 40px; margin-top: 72px; }
  @media (min-width: 900px) and (max-width: 1599px) { margin-top: 120px; gap: 84px; }
  @media (min-width: 1600px) { max-width: 1600px; margin-top: 180px; gap: 96px; }
`;

const PlansLeftColumn = styled.div`
  flex: 0 0 35%; position: sticky; top: 48px;
  display: flex; flex-direction: column; align-items: flex-start;
  @media (max-width: 1024px) { position: static; flex: 0 0 36%; }
  @media (max-width: 900px)  { width: 100%; }
  @media (min-width: 1600px) { top: 96px; }
`;

const PlansTitle = styled.h2`
  font-family: 'Playfair Display', serif; font-weight: 600; font-style: italic;
  font-size: 50px; line-height: 1.15; color: #121212; margin: 0 0 10px; text-align: left;
  @media (max-width: 1200px) { font-size: 54px; }
  @media (max-width: 1024px) { font-size: 40px; }
  @media (max-width: 768px)  { font-size: 30px; line-height: 1.08; }
  @media (max-width: 480px)  { font-size: 25px; }
  @media (min-width: 1600px) { font-size: 65px; }
`;

const PlansTitleGradient = styled.span`
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; color: transparent;
`;

const PlansSubtitle = styled.div`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif; font-size: 28px; line-height: 140%;
  color: rgba(0,0,0,1); text-transform: uppercase;
  display: flex; align-items: center; justify-content: flex-start; gap: 12px; margin-top: 8px;
  @media (max-width: 1024px) { font-size: 24px; }
  @media (max-width: 768px)  { font-size: 18px; gap: 8px; }
  @media (max-width: 480px)  { font-size: 15px; gap: 6px; }
  @media (min-width: 1600px) { font-size: 34px; }
`;

const PlansSubtitleDot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%); display: inline-block;
`;

const PlansSubtext = styled.p`
  font-family: 'Poppins', sans-serif; font-size: 18px; font-weight: 400; line-height: 32px;
  color: #666; margin-top: 16px; text-align: left;
  @media (max-width: 1024px) { font-size: 16px; line-height: 1.6; }
  @media (max-width: 768px)  { font-size: 14px; }
  @media (min-width: 1600px) { font-size: 20px; line-height: 1.6; }
`;

const PlansRightColumn = styled.div`
  flex: 0 0 60%; display: flex; flex-direction: column; gap: 32px; width: 100%;
  perspective: 1200px; perspective-origin: 50% 35%;
  @media (max-width: 1024px) { flex: 0 0 62%; }
  @media (max-width: 900px)  { flex: 1; }
  @media (max-width: 768px)  { gap: 20px; overflow: visible; }
  @media (min-width: 1600px) { gap: 40px; }
`;

const PlanCard = styled.div`
  background: ${p => p.$isMiddleCard ? 'linear-gradient(144.01deg,rgba(255,201,81,0.13) 0%,rgba(201,77,170,0.13) 100%)' : '#ffffff'};
  border-radius: 16px; border: 1px solid #eaeaea; padding: 48px 32px 18px;
  position: relative; overflow: visible; box-shadow: 0 10px 40px rgba(0,0,0,0.03);
  display: flex; flex-direction: column; gap: 18px; width: 100%; box-sizing: border-box;
  transform-style: preserve-3d; will-change: transform;
  @media (max-width: 768px)  { padding: 48px 18px 18px; gap: 16px; }
  @media (max-width: 360px)  { width: min(100%,290px); padding: 48px 14px 14px; gap: 12px; }
  @media (max-width: 1024px) { padding: 48px 24px 8px; gap: 22px; }
  @media (min-width: 1600px) { padding: 48px 48px 16px; border-radius: 24px; gap: 36px; }
`;

const CardContent = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  width: 100%; z-index: 1; position: relative; gap: 24px;
  @media (max-width: 768px) { flex-direction: column; gap: 12px; }
`;

const CardLeft = styled.div`
  flex: 0 0 40%; display: flex; flex-direction: column; align-items: flex-start; min-width: 0;
  @media (max-width: 768px) { width: 100%; flex: 0 0 auto; }
`;

const PlanHeader = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; width: 100%;
  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

const PlanMeta = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; min-width: 0;
  @media (max-width: 768px) { flex: 1; }
`;

const CrownBadge = styled.img`
  width: 48px; height: 48px; object-fit: contain; 
  @media (min-width: 1600px) { width: 64px; height: 64px; }
  @media (max-width: 768px)  { width: 34px; height: 34px; margin-bottom: 0; }
`;

const PlanName = styled.h3`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 26px; font-weight: 700; color: #121212; margin: 0 0 12px; 
  @media (min-width: 1600px) { font-size: 34px; margin-bottom: 18px; }
  @media (max-width: 768px)  { font-size: 18px; margin-bottom: 8px; }
`;

const PriceContainer = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px;
  @media (min-width: 1600px) { gap: 10px; margin-bottom: 18px; }
  @media (max-width: 768px)  { gap: 4px; margin-bottom: 8px; }
`;

const PriceRow = styled.div`
  display: flex; align-items: baseline; gap: 4px; flex-wrap: wrap;
`;

const PriceVal = styled.span`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif; font-size: 20px; font-weight: 700; color: #121212;
  @media (min-width: 1600px) { font-size: 26px; }
  @media (max-width: 768px)  { font-size: 15px; }
`;

const PricePeriod = styled.span`
  font-family: 'Gilroy-Regular', 'Poppins', sans-serif; font-size: 13px; color: #8c8c8c;
  @media (min-width: 1600px) { font-size: 16px; }
  @media (max-width: 768px)  { font-size: 10px; }
`;

const CardRight = styled.div`
  flex: 0 0 50%; align-self: center;
  background: ${p => p.$isMiddleCard ? 'rgba(255,255,255,1)' : 'rgba(245,245,245,1)'};
  border-radius: 16px; padding: 18px 16px; margin: 16px 20px 16px 40px;
  width: calc(100% - 76px); box-sizing: border-box;
  @media (max-width: 1024px) { margin: 8px 0 8px 18px; width: calc(100% - 18px); padding: 16px 14px; }
  @media (max-width: 768px)  { padding: 10px 8px; margin-left: 0; width: 100%; align-self: stretch; }
  @media (max-width: 360px)  { padding: 8px 6px; margin: 0; width: 100%; }
  @media (min-width: 1600px) { padding: 24px 32px; border-radius: 20px; }
`;

const FeaturesList = styled.ul`
  list-style: none; padding: 0 0 0 14px; margin: 0;
  display: flex; flex-direction: column; gap: 18px;
  @media (min-width: 1600px) { gap: 20px; }
  @media (max-width: 768px)  { gap: 10px; padding-left: 8px; }
  @media (max-width: 360px)  { gap: 8px; padding-left: 6px; }
`;

const FeatureItem = styled.li`
  display: flex; align-items: center; gap: 10px;
  @media (min-width: 1600px) { gap: 14px; }
  @media (max-width: 768px)  { gap: 8px; }
`;

const FeatureBullet = styled.span`
  width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-size: 13px; font-weight: 700; line-height: 1;
  @media (min-width: 1600px) { width: 26px; height: 26px; font-size: 16px; }
  @media (max-width: 768px)  { width: 16px; height: 16px; font-size: 11px; }
  @media (max-width: 360px)  { width: 14px; height: 14px; font-size: 10px; }
`;

const FeatureText = styled.span`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 15px; font-weight: 700; color: #000; line-height: 26px;
  @media (min-width: 1600px) { font-size: 18px; }
  @media (max-width: 768px)  { font-size: 12px; }
  @media (max-width: 360px)  { font-size: 11px; }
`;


const CardButtonWrapper = styled.div`
  width: 100%; display: flex; justify-content: center; z-index: 1; position: relative;
`;

const ChoosePlanBtn = styled.button`
  width: min(100%, 630px); height: 48px; border-radius: 999px; border: 2px solid transparent;
  background: linear-gradient(#ffffff,#ffffff) padding-box, linear-gradient(90.59deg,#ffc951 0%,#c94daa 100%) border-box;
  color: #000; font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 19px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center;
  @media (min-width: 1600px) { height: 56px; font-size: 22px; }
  @media (max-width: 768px)  { height: 40px; font-size: 14px; }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  display: flex; justify-content: center; align-items: center; z-index: 9999;
  opacity: ${p => p.$isOpen ? 1 : 0}; pointer-events: ${p => p.$isOpen ? 'all' : 'none'};
  transition: opacity 0.3s ease;
`;

const ModalContent = styled.div`
  background: #fff; border-radius: 24px; width: 90%; max-width: 480px;
  box-sizing: border-box; padding: 32px;
  box-shadow: 0 24px 60px rgba(201,77,170,0.15); border: 1px solid rgba(201,77,170,0.12);
  transform: ${p => p.$isOpen ? 'scale(1)' : 'scale(0.9)'};
  transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); overflow: visible;
  @media (max-width: 480px) { padding: 24px 20px; }
`;

const ModalHeader = styled.div`
  margin-bottom: 20px; text-align: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700; margin: 0 0 8px;
  background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; color: transparent;
`;

const RadioGroup = styled.div`
  display: flex; background: #f5f5f7; padding: 4px; border-radius: 999px;
  margin-bottom: 20px; border: 1px solid #eaeaea;
`;

const RadioOption = styled.label`
  flex: 1; text-align: center; padding: 10px 16px;
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif; font-size: 14px; font-weight: 600;
  color: ${p => p.$active ? '#121212' : '#666'};
  background: ${p => p.$active ? '#ffffff' : 'transparent'};
  border-radius: 999px; cursor: pointer; transition: all 0.25s ease; user-select: none;
  box-shadow: ${p => p.$active ? '0 4px 12px rgba(0,0,0,0.05)' : 'none'};
  input { display: none; }
`;

const PriceDisplay = styled.div`
  text-align: center; margin-bottom: 20px;
`;

const PriceLabel = styled.div`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif; font-size: 12px; color: #666;
  text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px;
`;

const PriceValue = styled.div`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif; font-size: 32px; font-weight: 700;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; color: transparent;
`;

const FormField = styled.div`
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px;
`;

const FormLabel = styled.label`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif; font-size: 14px; font-weight: 600; color: #444;
`;

const FormInput = styled.input`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif; font-size: 14px; padding: 10px 14px;
  border-radius: 10px; border: 1px solid ${p => p.$error ? '#ff4d4f' : '#eaeaea'};
  background: #fafafa; color: #121212; transition: all 0.2s ease;
  &:focus { outline: none; border-color: #c94daa; background: #fff; box-shadow: 0 0 0 3px rgba(201,77,170,0.15); }
`;

const ErrorMsg = styled.span`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif; font-size: 11px; color: #ff4d4f; margin-top: 2px;
`;

const ButtonRow = styled.div`
  display: flex; gap: 12px; margin-top: 24px;
`;

const ModalButton = styled.button`
  flex: 1; padding: 12px; border-radius: 12px;
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif; font-size: 15px; font-weight: 600;
  cursor: pointer; transition: all 0.25s ease; border: none; box-sizing: border-box;
`;

const CancelBtn = styled(ModalButton)`
  background: #f5f5f7; color: #555;
  &:hover { background: #eaeaec; color: #333; }
`;

const BuyBtn = styled(ModalButton)`
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
  color: #fff; box-shadow: 0 8px 20px rgba(201,77,170,0.2);
  opacity: ${p => p.disabled ? 0.7 : 1}; cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 24px rgba(201,77,170,0.3); }
  &:active:not(:disabled) { transform: translateY(0); }
`;

const SuccessContainer = styled.div`
  display: flex; flex-direction: column; align-items: center; text-align: center; padding: 24px 8px;
`;

const SuccessIcon = styled.div`
  width: 64px; height: 64px; border-radius: 50%; background: rgba(76,175,80,0.1);
  color: #4caf50; font-size: 32px; font-weight: bold;
  display: flex; justify-content: center; align-items: center; margin-bottom: 16px;
`;

const SuccessTitle = styled.h4`
  font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 700;
  color: #121212; margin: 0 0 12px;
`;

const SuccessText = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif; font-size: 15px;
  color: #666; line-height: 1.5; margin: 0 0 24px;
`;